export type AnalyticsSubjectKind =
  | 'PRODUCT'
  | 'VARIANT'
  | 'LISTING'
  | 'STORE'
  | 'RETAILER'
  | 'CATEGORY'
  | 'BRAND'
  | 'CART'
  | 'LEAD'
  | 'MARKET'
  | 'SEARCH'
  | 'MEMBERSHIP';

export interface AnalyticsEvent {
  readonly name: string;
  readonly subjectKind?: AnalyticsSubjectKind;
  readonly subjectId?: string;
  readonly marketId?: string | null;
  readonly serviceAreaId?: string | null;
  /** Only non-sensitive, aggregate event metadata belongs here. */
  readonly properties?: Readonly<Record<string, unknown>>;
}

export interface CustomerAnalyticsClient {
  configure(input: { readonly enabled: boolean; readonly signedIn: boolean }): void;
  /** Lets a consent control take effect immediately without a page reload. */
  setAnalyticsConsent?(granted: boolean): void;
  captureInitialAttribution(): Promise<void>;
  track(event: AnalyticsEvent): Promise<void>;
}

export interface CustomerAnalyticsClientOptions {
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  readonly location?: Pick<Location, 'href'> | null;
  readonly referrer?: () => string;
}

const VISITOR_ID_KEY = 'beauty-burst.analytics.visitor-id';
const ANALYTICS_CONSENT_PATH = '/api/v1/customer/account/consents';

function endpoint(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/u, '')}${path}`;
}

function generatedVisitorId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid === undefined
    ? `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`
    : `visitor-${uuid}`;
}

function visitorId(storage: CustomerAnalyticsClientOptions['storage']): string {
  try {
    const stored = storage?.getItem(VISITOR_ID_KEY);
    if (stored !== null && stored !== undefined && stored.length >= 8 && stored.length <= 128)
      return stored;
    const next = generatedVisitorId().slice(0, 128);
    storage?.setItem(VISITOR_ID_KEY, next);
    return next;
  } catch {
    return generatedVisitorId().slice(0, 128);
  }
}

function campaign(location: CustomerAnalyticsClientOptions['location']) {
  const href = location?.href;
  const search = href === undefined ? new URLSearchParams() : new URL(href).searchParams;
  return {
    utmSource: search.get('utm_source') ?? undefined,
    utmMedium: search.get('utm_medium') ?? undefined,
    utmCampaign: search.get('utm_campaign') ?? undefined,
    utmContent: search.get('utm_content') ?? undefined,
    utmTerm: search.get('utm_term') ?? undefined,
  };
}

function consentGranted(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  const latest = value.find(
    (entry) =>
      entry !== null &&
      typeof entry === 'object' &&
      (entry as { consent_type?: unknown }).consent_type === 'ANALYTICS',
  ) as { granted?: unknown } | undefined;
  return latest?.granted === true;
}

/**
 * First-party browser analytics only. Events are deliberately best effort:
 * every transport failure resolves, so analytics cannot interrupt shopping.
 */
export function createCustomerAnalyticsClient(
  options: CustomerAnalyticsClientOptions = {},
): CustomerAnalyticsClient {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const baseUrl = options.baseUrl ?? env?.VITE_CUSTOMER_API_BASE_URL ?? '';
  const fetchImpl = options.fetchImpl ?? fetch;
  const storage =
    options.storage === undefined
      ? typeof window === 'undefined'
        ? null
        : window.sessionStorage
      : options.storage;
  const location =
    options.location === undefined
      ? typeof window === 'undefined'
        ? null
        : window.location
      : options.location;
  const referrer =
    options.referrer ?? (() => (typeof document === 'undefined' ? '' : document.referrer));
  const id = visitorId(storage);
  let enabled = false;
  let signedIn = false;
  let consent: boolean | undefined;
  let attributionCaptured = false;

  const post = async (path: string, body: unknown): Promise<void> => {
    try {
      await fetchImpl(endpoint(baseUrl, path), {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Idempotency-Key': globalThis.crypto?.randomUUID?.() ?? `analytics-${Date.now()}`,
        },
        body: JSON.stringify(body),
      });
    } catch {
      // Instrumentation must never make customer behaviour fail.
    }
  };

  const analyticsConsent = async (): Promise<boolean> => {
    if (!enabled || !signedIn) return false;
    if (consent !== undefined) return consent;
    try {
      const response = await fetchImpl(endpoint(baseUrl, ANALYTICS_CONSENT_PATH), {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      consent = response.ok ? consentGranted(await response.json()) : false;
    } catch {
      consent = false;
    }
    return consent;
  };

  return {
    configure(input) {
      enabled = input.enabled;
      if (signedIn !== input.signedIn) consent = undefined;
      signedIn = input.signedIn;
    },
    setAnalyticsConsent(granted) {
      consent = granted;
    },
    async captureInitialAttribution() {
      if (!enabled || attributionCaptured) return;
      attributionCaptured = true;
      const page = location?.href;
      await post('/api/v1/analytics/attribution', {
        visitorId: id,
        touchKind: 'FIRST',
        ...campaign(location),
        ...(referrer() === '' ? {} : { referrer: referrer() }),
        ...(page === undefined ? {} : { landingPage: page }),
      });
    },
    async track(event) {
      const allowed = await analyticsConsent();
      if (!allowed) return;
      await post('/api/v1/analytics/events', {
        name: event.name,
        visitorId: id,
        consentAnalytics: true,
        surface: 'WEB',
        ...(event.subjectKind === undefined ? {} : { subjectKind: event.subjectKind }),
        ...(event.subjectId === undefined ? {} : { subjectId: event.subjectId }),
        ...(event.marketId === undefined || event.marketId === null
          ? {}
          : { marketId: event.marketId }),
        ...(event.serviceAreaId === undefined || event.serviceAreaId === null
          ? {}
          : { serviceAreaId: event.serviceAreaId }),
        ...(event.properties === undefined ? {} : { properties: event.properties }),
      });
    },
  };
}

let defaultClient: CustomerAnalyticsClient | undefined;

export function customerAnalytics(): CustomerAnalyticsClient {
  defaultClient ??= createCustomerAnalyticsClient();
  return defaultClient;
}
